import { Button, Card, CardContent, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HeadingWithDescription from '../../components/headingWithDescription';
import { AccountInformation, Address, MachineDetails } from '../../types/manageAccount.type';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ManageAccountService from '../../services/manageAccount.service';
import ChooseAppliance from '../Amc/ChooseAppliance';
import { EditOutlined } from '@mui/icons-material';
import ActivityIndicator from '../../components/activityIndicator';
import { RaiseRequestServices, ReasonsResponse } from '../../services/raiseRequest.services';
import { toast } from 'react-toastify';
import AddressDetails from './AddressDetails';
import MachineTab from './MachineTab';
import { ReasonSelector } from './ReasonSelector';
import { IoArrowBack } from 'react-icons/io5';
import { CreateRequestDTO } from '../../types/raiseRequest.types';

function DealerDetails({ userDetails }: { userDetails: AccountInformation | null }) {
  return (
    <Stack borderRadius={5} direction={'row'}>
      <Stack flex={1}>
        <Typography fontWeight={'bold'}>Dealer Details</Typography>
        <Typography variant="caption">Select Dealer</Typography>
      </Stack>
      <IconButton onClick={() => {}}>
        <EditOutlined />
      </IconButton>
    </Stack>
  );
}

enum ServiceType {
  'Installation / Demo' = 'Installation / Demo',
  'Relocation' = 'Relocation',
  'Service' = 'Service',
}

export default function RaiseRequestScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedMachine, setSelectedMachine] = useState<MachineDetails | null>(null);
  const [machines, setMachines] = useState<MachineDetails[]>([]);
  const [selectedServiceType, setServiceType] = useState<ServiceType>(ServiceType['Service']);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [selectedReasonIndex, setSelectedReasonIndex] = useState<number>(-1);
  const [reasons, setReasons] = useState<ReasonsResponse[]>([]);
  const [userDetails, setUserDetails] = useState<AccountInformation | null>(null);
  const [prevAddress, setPreviousAddress] = useState<Address[] | null>(null);
  const [loading, setLoading] = useState(true);
  const userId = useSelector((state: RootState) => state.auth.userId);
  const token = useSelector((state: RootState) => state.auth.token);
  const [isEditing, setEditing] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const isUpdated =
        JSON.stringify(userDetails?.address_details) !== JSON.stringify(prevAddress);

      if (!selectedMachine) {
        toast.error('Please select a machine');
        return;
      }

      if (!selectedReason) {
        toast.error('Please select a reason');
        return;
      }

      const address = userDetails?.address_details[0];

      const data: CreateRequestDTO = {
        source_id: 'MS',
        serviceRequestId: 0,
        model: selectedMachine?.zzr3mat_id,
        mobile: userDetails?.rcn ?? '',
        serial_no: selectedMachine?.zzr3ser_no,
        issue_text: 'NA',
        product: selectedMachine?.machine_code ?? '',
        first_name: userDetails?.first_name ?? '',
        last_name: userDetails?.last_name ?? '',
        house_no: address?.house_no ?? '',
        area: address?.street ?? '',
        street: 'NA',
        address: `${address?.house_no} ${address?.line1} ${address?.line2} ${address?.line3} ${address?.street} ${address?.city}`,
        pincode: address?.zipcode ?? '',
        email: userDetails?.email ?? '',
        customer_id: userDetails?.cust_code ?? '',
        new_addr: isUpdated ? 'X' : '',
        problem_category:
          selectedReasonIndex !== -1 ? reasons[selectedReasonIndex].ProblemCode : '',
        preferreddate: '',
        preferredtime: '',
      };

      if (!token) {
        navigate('/');
        console.error('Token is null');
        return;
      }

      const response = await RaiseRequestServices.createRequest(data, token);
      setMessage(response?.message ?? '');
    } catch (error) {
      console.log('Error in handleSubmit at RaiseRequest', error);
    } finally {
      setLoading(false);
    }
  };

  const getMachinesDetails = async () => {
    if (!userId || !token) return;
    try {
      setLoading(true);
      const res = await ManageAccountService.getAccountInfoAsync(userId, token);
      const data = res?.machine_details;
      if (data) {
        setMachines(data);
        setUserDetails(res);
        setPreviousAddress(res?.address_details ?? []);
      }
    } catch (error) {
      console.log('Error in getMachinesDetails', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    userId && getMachinesDetails();
    // eslint-disable-next-line
  }, [userId]);

  useEffect(() => {
    if (searchParams.get('zzr3mat_id')) {
      setCurrentStep(2);
      setSelectedMachine(
        machines.find((machine) => machine?.zzr3mat_id === searchParams.get('zzr3mat_id')) ?? null,
      );
      setServiceType(ServiceType['Service']);
    }
    // eslint-disable-next-line
  }, [machines]);

  useEffect(() => {
    if (selectedMachine) {
      setSearchParams({ zzr3mat_id: selectedMachine?.zzr3mat_id });
      RaiseRequestServices.getReasonsByProductCode(selectedMachine?.machine_code ?? '').then(
        setReasons,
      );
    }
    // eslint-disable-next-line
  }, [selectedMachine]);

  if (message?.length) {
    return (
      <Stack p={2} gap={2}>
        <Card>
          <CardContent>
            <Typography>{message}</Typography>
          </CardContent>
        </Card>
        <Button
          onClick={() => navigate('/')}
          sx={{ textTransform: 'none' }}
          variant="contained"
          startIcon={<IoArrowBack />}
        >
          Back to Homepage
        </Button>
      </Stack>
    );
  }

  return (
    <Stack p={2} gap={2} flex={1}>
      {loading && <ActivityIndicator />}
      <HeadingWithDescription heading="Raise Request" />
      {currentStep === 1 && (
        <ChooseAppliance
          showHeading={false}
          setCurrentStep={setCurrentStep}
          setLoading={setLoading}
          setSelectedMachine={setSelectedMachine}
          machinesDetails={machines}
          selectedMachine={selectedMachine}
        />
      )}
      {currentStep === 2 && (
        <>
          <Stack flex={1} gap={2}>
            <MachineTab selectedMachine={selectedMachine} setCurrentStep={setCurrentStep} />
            <Divider />

            {selectedServiceType === ServiceType['Service'] && (
              <ReasonSelector
                reasons={reasons}
                selectedReason={selectedReason}
                setSelectedReason={setSelectedReason}
                setSelectedReasonIndex={setSelectedReasonIndex}
              />
            )}

            <Divider />
            {selectedServiceType !== ServiceType['Relocation'] && (
              <AddressDetails
                userDetails={userDetails}
                setUserDetails={setUserDetails}
                isEditing={isEditing}
                setEditing={setEditing}
              />
            )}
            {selectedServiceType === ServiceType['Installation / Demo'] && (
              <DealerDetails userDetails={userDetails} />
            )}
          </Stack>
          <Button
            size="large"
            sx={{ backgroundColor: 'black', py: 2, borderRadius: 3, textTransform: 'none' }}
            variant="contained"
            disabled={!selectedMachine || !selectedReason || isEditing}
            onClick={handleSubmit}
          >
            Raise Request
          </Button>
        </>
      )}
    </Stack>
  );
}
